import BluebirdPromise from 'bluebird';
import {
  OperationToQuickloadUrl,
  QuickLoad,
  getPreloadsFromPath,
  getQueryByOperationName,
  cacheFactory,
} from '@trello/quickload';
import { loadApiDataFromUrl } from './loadApiDataFromUrl';
import { ModelCache } from './model-cache';
import { ninvoke } from 'app/scripts/lib/util/ninvoke';
import type { MappingRules } from './db.types';

/**
 * Will load data from the quickload cache. If using this, you want to align the queryName
 * you pass with an existing quickload query, such as MemberHeader. We use operation names to
 * denote which query you are loading, specified as the name of the query in the graphql file.
 * @param queryName: MemberHeader, MemberBoards, etc.
 * @param traceId: traceId used for vital stats
 * @param idModel: the model that quickload we will replace the url string with. For example, for
 * CurrentBoardFull we replace the :idBoard param with the shortLink
 * @param modelType: Member, Board, etc
 * @param mappingRules: defines how to modify data before adding to the model cache
 * @returns
 */
export const loadApiDataFromQuickLoad = (
  queryName: keyof typeof OperationToQuickloadUrl,
  {
    traceId,
    idModel,
    isHeaderLoad,
    modelType,
    mappingRules,
  }: {
    traceId?: string | null;
    idModel?: string;
    isHeaderLoad?: boolean | null;
    modelType: string;
    mappingRules?: MappingRules;
  },
) => {
  const operationDefinition = OperationToQuickloadUrl[queryName];

  if (!operationDefinition) {
    throw new Error(`${queryName} quickload definition was removed or renamed`);
  }

  const { param } = getPreloadsFromPath();

  if (!idModel) {
    if (operationDefinition.rootId === ':idMember') {
      idModel = 'me';
    } else if (param) {
      idModel = param;
    } else {
      // this can't really happen, unless someone passes the wrong idModel and there is no param
      // as a result. Throwing an error should stop this in development, but don't want to throw
      // in prod
      if (process.env.NODE_ENV !== 'production') {
        throw new Error(
          `Could not find param for ${queryName}. Please ensure correct idModel is being passed.`,
        );
      }

      return BluebirdPromise.resolve();
    }
  }

  let apiUrl = operationDefinition.url.replace(
    operationDefinition.rootId,
    idModel,
  );

  if (new RegExp('^/1/search').test(apiUrl)) {
    const dscToken = /dsc=([^;]+)/.exec(document.cookie)?.[1];
    // We probably shouldn't be setting this to the string "undefined", but this
    // was to preserve the functionality of the code after conversion to TypeScript.
    apiUrl += `&dsc=${dscToken ?? 'undefined'}`;
  }

  return loadApiDataFromUrl(apiUrl, {
    modelType,
    operationName: operationDefinition.operationName,
    traceId: traceId || QuickLoad.getPreloadTraceId(),
    isHeaderLoad: Boolean(isHeaderLoad),
  })
    .spread((data) => {
      if (data) {
        return ninvoke(
          ModelCache,
          'enqueueDelta',
          modelType,
          data,
          mappingRules,
          {
            document: getQueryByOperationName(queryName),
            // the useQuickLoad hook will take care of syncing data to the apollo cache
            // once this is 'complete' then we can start syncing these deltas to apollo
            skipSyncingToApollo: QuickLoad.status === 'pending',
          },
        );
      } else {
        return [];
      }
    })
    .then((results) => {
      /**
       * At this point we can mark queries hydrated to both caches. That is because
       * the enqueueDelta does a syncDeltaToCache that is synchronous. If that wasn't,
       * we'd want to change this so that there isn't a race between the useQuickload
       * hook and loading from here.
       */
      cacheFactory.markQueryHydratedFor(queryName, 'ModelCache');
      cacheFactory.markQueryHydratedFor(queryName, 'Apollo');
      return results;
    })
    .catch((err) => {
      cacheFactory.markQueryHydratedFor(queryName, 'ModelCache');
      cacheFactory.markQueryHydratedFor(queryName, 'Apollo');
      throw err;
    });
};
